import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Card, Row, Col, Modal, Form, Spinner, Button, InputGroup, FormLabel, ListGroup, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaMapMarkerAlt, FaPlayCircle, FaSearch, FaFileExport, FaAngleRight, FaAngleLeft, FaAngleDoubleRight, FaAngleDoubleLeft } from 'react-icons/fa';
import {
  useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel, getPaginationRowModel, flexRender
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { GoogleMapsProvider } from '../../../../../providers/GoogleMapsProvider';
import { CamarasService } from '../../../../../services/apiService';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import * as XLSX from 'xlsx';
import { jwtDecode } from 'jwt-decode';
import { obtenerIconoAlerta, obtenerIconoAlertaAI, obtenerIconoAlertaOmisiones, obtenerIconoEvento, obtenerImgAlerta, obtenerImgAlertaCamara, obtenerImgAlertaOmisiones } from '../../../../../components/common/google-maps-selector/IconosTabla';
import iconoPng from '../../../../../assets/img/icons/export.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from '../../../../../components/common/Flex';
import alertasFiltrosStore from '../../../../../lib/store/alertas_filtros_store';
import { iAlerta, iFecha, iHhora, iRuta, iUsuario, iVelocidad, obtenerInfoView, obtenerInfoViewTop } from '../../../../../components/common/google-maps-selector/GoogleMapSelector';
import { generateExcelReport } from '../../../../../helpers/createExcelReport';

// Carga diferida de componentes pesados
const GoogleMapSelector = lazy(() => import('../../../../../components/common/google-maps-selector/GoogleMapSelector'));
const CamaraVisor = lazy(() => import('../../../../../components/camaras/CamaraVisor'));

// Activamos el soporte para formatos personalizados
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.locale('es');

const ROW_HEIGHT = 65;
const HEADER_HEIGHT = 40;

// Mini-componente para celda de coordenadas con feedback "Copiado!"
const CopyCoordCell = ({ lat, lng, style }) => {
  const [copied, setCopied] = React.useState(false);
  const coords = `${lat}, ${lng}`;
  return (
    <div className='px-1' style={style} data-cell="coordenadas">
      <OverlayTrigger placement="top" overlay={<Tooltip>{copied ? 'Copiado!' : 'Clic para copiar'}</Tooltip>}>
        <span
          style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
          onClick={() => {
            navigator.clipboard.writeText(coords);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {parseFloat(lat || 0).toFixed(5)}, {parseFloat(lng || 0).toFixed(5)}
        </span>
      </OverlayTrigger>
    </div>
  );
};

// Estilos personalizados para la tabla
const customStyles = `
.list-striped .alert-row:nth-child(odd) {
  background-color: rgba(250, 250, 250, 0.9);
}
.list-striped .alert-row:nth-child(even) {
  background-color: white;
}
.list-striped .alert-row:hover {
  background-color: rgba(245, 245, 245, 1);
}
.table-header {
  font-weight: 600;
  font-size: 0.83rem;
  position: sticky;
  top: 0;
  z-index: 1;
}
.alert-row {
  padding: 0.5rem 0.5rem;
  font-size: 0.83rem;
  transition: background-color 0.15s ease;
}

/* ✅ ESTILOS ESPECÍFICOS PARA IMPRESIÓN */
.print-mode {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}

.print-mode .alert-row {
  position: relative !important;
  transform: none !important;
  display: flex !important;
}

[data-print-mode="true"] {
  page-break-inside: avoid;
}

@media print {
  .print-mode {
    height: auto !important;
    overflow: visible !important;
  }
  
  .alert-row {
    page-break-inside: avoid;
    position: relative !important;
    transform: none !important;
  }
  
  [data-no-print="true"] {
    display: none !important;
  }
}
`;

// Función para formatear fechas
const formatFecha = (fechaStr, title = '') => {
  if (!fechaStr || typeof fechaStr !== 'string') return 'N/A';

  try {
    let fechaParseada;

    // ✅ DETECTAR SI ES OMISIONES POR EL TÍTULO O ESTRUCTURA DE DATOS
    const esOmisiones = title.toLowerCase().includes('omisiones') ||
      title.toLowerCase().includes('omision');

    if (esOmisiones) {
      fechaParseada = dayjs.utc(fechaStr).local();
    } else {
      // ✅ PARA GPS/CÁMARAS: Formato estándar local
      fechaParseada = dayjs(fechaStr, 'YYYY-MM-DD HH:mm:ss');
      //console.log(`[formatFecha] GPS/CÁMARAS - Fecha local: ${fechaStr}`);
    }

    // ✅ VERIFICAR VALIDEZ
    if (!fechaParseada.isValid()) {
      //console.warn(`[formatFecha] Fecha inválida: ${fechaStr}`);
      return fechaStr;
    }

    const resultado = fechaParseada.format('DD/MM/YYYY HH:mm:ss');
    //console.log(`[formatFecha] Resultado final: ${resultado}`);

    return resultado;
  } catch (error) {
    console.error(`[formatFecha] Error al formatear fecha: ${fechaStr}`, error);
    return fechaStr;
  }
};

const AlertaTableListTanstack = ({
  data = [],
  columns = [],
  searchValue = '',
  onSearch,
  pageSize = 100,
  onPageSizeChange,
  isLoading = false,
  title = 'Listado de Alertas',
  isGroup = false,
  isExporting = false,
  onExport,
  onTableRendered,
  showExtendedColumns = false,
}) => {
  const isOmisiones = title.toLowerCase().includes('omisiones');
  const store = alertasFiltrosStore();
  // Estados para la tabla
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState(searchValue);
  const [inputValue, setInputValue] = useState(searchValue);
  const [filtroAtendido, setFiltroAtendido] = useState('todos');
  const debounceTimeout = useRef(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: pageSize,
  });

  // Estados para modales y video
  const [infoView, setInfoView] = useState(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoChecks, setVideoChecks] = useState([false, false, false, false]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [urlVideo, setUrlVideo] = useState(["", "", "", ""]);
  const [consultandoCamara, setConsultandoCamara] = useState([false, false, false, false]);

  const videoIds = useMemo(() => ["canal-1", "canal-2", "canal-3", "canal-4"], []);
  const MySwal = withReactContent(Swal);

  const [showMarcadorModal, setShowMarcadorModal] = useState(false);
  const [contentMarcadorModal, setContentMarcadorModal] = useState([]);
  const [geocercasFiltradas, setGeocercasFiltradas] = useState([]);

  // Sincronizar el filtro global con el searchValue recibido por props
  React.useEffect(() => {
    setGlobalFilter(searchValue);
  }, [searchValue]);

  // Sincronizar el tamaño de página con el pageSize recibido por props
  React.useEffect(() => {
    setPagination(prev => ({
      ...prev,
      pageSize
    }));
  }, [pageSize]);

  React.useEffect(() => {
    if (typeof onTableRendered === 'function') {
      onTableRendered(data.length);
    }
  }, [data.length]);

  // Definir las columnas con renderizado personalizado
  const tableColumns = useMemo(() => {
    // Columnas por defecto basadas en la estructura del AlertaTableList original
    const fechaColumn = {
      id: 'fecha',
      header: 'Fecha',
      accessorKey: 'fecha',
      cell: ({ row }) => (
        <div style={{ fontWeight: '500' }}>
          {formatFecha(row.original.fecha, title)}  {/* ✅ Pasar title */}
        </div>
      ),
      size: 150
    };

    const alertaColumn = {
      id: 'alerta',
      header: 'Alerta',
      accessorKey: 'alerta',
      cell: ({ row }) => (
        <div className="d-flex align-items-center">
          {isGroup ? (
            <div>
              <strong>{row.original.alerta_real}</strong>
              <br />
              <span>{row.original.alerta}</span>
            </div>
          ) : (
            <div>
              <strong>{row.original.alerta}</strong>
            </div>
          )}
        </div>
      ),
      size: 250 // ~25%
    };

    const vehiculoColumn = {
      id: 'no_economico',
      header: 'Vehículo',
      accessorKey: 'no_economico',
      size: 120 // ~12%
    };

    const randomColumn = {
      id: 'random',
      header: 'Random',
      accessorKey: 'random',
      cell: ({ row }) => row.original?.random ?? 'N/A',
      size: 100
    };

    const tiempoColumn = {
      id: 'tiempo_abierto',
      header: 'Tiempo abierto',
      accessorKey: 'tiempo_abierto',
      cell: ({ row }) => {
        const isClosed = (row.original?.alerta || '').toLowerCase().includes('cerrad');
        if (!isClosed) return '--';
        return row.original?.tiempo_abierto || '--';
      },
      size: 120
    };

    const choferColumn = {
      id: 'chofer',
      header: 'Chofer',
      accessorKey: 'chofer',
      cell: ({ row }) => row.original.chofer || 'N/A',
      size: 150 // ~15%
    };

    const geocercaColumn = {
      id: 'geocerca',
      header: 'Geocerca',
      accessorKey: 'geocerca',
      cell: ({ row }) => row.original.geocerca || 'N/A',
      size: 150 // ~15%
    };

    const coordenadasColumn = {
      id: 'coordenadas',
      header: 'Coordenadas',
      accessorKey: 'coordenadas',
      cell: ({ row }) => (
        <CopyCoordCell lat={row.original.latitud} lng={row.original.longitud} style={{}} />
      ),
      size: 160
    };

    const ubicacionColumn = {
      id: 'ubicacion',
      header: 'Mapa',
      accessorKey: 'ubicacion',
      cell: ({ row }) => (
        <div className="text-center">
          <Button variant='default' size="sm" className="btn-light rounded-5 p-1" onClick={() => handleShowMap(row.original)}>
            <FaMapMarkerAlt className="text-danger" />
          </Button>
        </div>
      ),
      size: 60
    };

    const videoColumn = {
      id: 'video',
      header: 'Video',
      accessorKey: 'video',
      cell: ({ row }) => (
        <div className="text-center">
          {row.original.vehiculo_con_camara && row.original.camara_en_linea ? (
            <FaPlayCircle
              size={20}
              className="text-danger cursor-pointer"
              onClick={() => handleShowVideoModal(row.original)}
              style={{ cursor: 'pointer' }}
            />
          ) : (
            <span className="text-muted">N/A</span>
          )}
        </div>
      ),
      size: 80 // ~8%
    };

    const defaultColumns = [
      fechaColumn,
      alertaColumn,
      vehiculoColumn,
      ...(showExtendedColumns ? [randomColumn, tiempoColumn] : []),
      choferColumn,
      geocercaColumn,
      coordenadasColumn,
      ubicacionColumn,
      videoColumn
    ];

    // Si se proporcionan columnas personalizadas, utilizarlas
    if (columns && columns.length > 0) {
      return columns.map(column => {
        // Asegurarse de que cada columna tenga un ID
        const columnWithId = {
          ...column,
          id: column.id || column.accessorKey || column.accessor || `column-${Math.random()}`
        };

        // Aplicar renderizado personalizado para la columna de Alerta si es necesario
        if (columnWithId.accessorKey === 'alerta' || columnWithId.accessor === 'alerta') {
          return {
            ...columnWithId,
            cell: defaultColumns.find(c => c.id === 'alerta').cell
          };
        }
        if (showExtendedColumns && (columnWithId.accessorKey === 'random' || columnWithId.accessor === 'random')) {
          return {
            ...columnWithId,
            cell: defaultColumns.find(c => c.id === 'random').cell
          };
        }
        if (showExtendedColumns && (columnWithId.accessorKey === 'tiempo_abierto' || columnWithId.accessor === 'tiempo_abierto')) {
          return {
            ...columnWithId,
            cell: defaultColumns.find(c => c.id === 'tiempo_abierto').cell
          };
        }
        // Coordenadas con clic para copiar
        if (columnWithId.accessorKey === 'coordenadas' || columnWithId.id === 'coordenadas') {
          return {
            ...columnWithId,
            cell: defaultColumns.find(c => c.id === 'coordenadas')?.cell
          };
        }
        // Ubicación / Mapa con icono
        if (columnWithId.accessorKey === 'ubicacion' || columnWithId.id === 'ubicacion') {
          return {
            ...columnWithId,
            cell: defaultColumns.find(c => c.id === 'ubicacion')?.cell
          };
        }
        // Video con icono
        if (columnWithId.accessorKey === 'video' || columnWithId.id === 'video') {
          return {
            ...columnWithId,
            cell: defaultColumns.find(c => c.id === 'video')?.cell
          };
        }
        return columnWithId;
      });
    }

    return defaultColumns;
  }, [columns, isGroup, showExtendedColumns, title]);
  //}, [columns, isGroup]);

  // Configurar la tabla
  const tableGlobalFilterFn = useCallback((row, columnId, filterValue, addMeta) => {
    const original = row.original;

    // --- Lógica para Marcadores (replicando getLabelMarcador) ---
    const paramsEntry = {
      Clientes: (original.cliente || []).map(c => c.Nombres).join(',') || '',
      Geocercas: Array.isArray(original.geoceca) ? original.geoceca.map(c => c.Nombre).join(',') || '' : original.geoceca || '',
      Referencias: (original.referencia || []).map(c => c.Nombres).join(',') || ''
    };
    const cantidadMarcadores = Object.values(paramsEntry)
      .flatMap(str => (str || '').split(','))
      .filter(item => item && item.trim() !== '')
      .length;

    // Incluir el contenido de los marcadores o "n/a" si no existen.
    const marcadoresContent = cantidadMarcadores > 0
      ? Object.values(paramsEntry).join(' ')
      : 'n/a';

    // Construir una cadena con todo el contenido buscable de la fila
    const searchableRowContent = [
      original.fecha, // Fecha original sin formatear
      formatFecha(original.fecha, title), // Fecha formateada
      original.alerta,
      original.alerta_real,
      original.alerta_grupo,
      original.no_economico,
      original.chofer || 'n/a',
      marcadoresContent,
      original.vehiculo_con_camara && original.camara_en_linea ? 'video disponible' : 'n/a'
    ]
      .filter(Boolean) // Eliminar valores nulos o undefined
      .join(' ')
      .toLowerCase();

    const matches = searchableRowContent.includes(String(filterValue).toLowerCase());
    return matches;
  }, [title]);

  /*   const tableGlobalFilterFn = useCallback((row, columnId, filterValue) => {
      const safeFilterValue = filterValue.toLowerCase();
      const value = row.getValue(columnId);
   
      if (columnId === 'fecha') {
        const rawValue = String(value).toLowerCase();
        // Usamos la misma función de formateo que la celda para consistencia
        const formattedValue = formatFecha(value, title).toLowerCase();
        return rawValue.includes(safeFilterValue) || formattedValue.includes(safeFilterValue);
      }
   
      // Para las demás columnas, usamos la lógica por defecto
      return String(value).toLowerCase().includes(safeFilterValue);
    }, [title]); // Dependemos de 'title' porque formatFecha lo usa */

  // Filtro de Atendido para omisiones (client-side a nivel tabla)
  const filteredData = useMemo(() => {
    if (!isOmisiones || filtroAtendido === 'todos') return data;
    const valorFiltro = filtroAtendido === 'si' ? 1 : 0;
    return data.filter(row => row.Atendido === valorFiltro);
  }, [data, filtroAtendido, isOmisiones]);

  const table = useReactTable({
    data: filteredData,
    columns: tableColumns,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    globalFilterFn: tableGlobalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSorting: true,
    enableGlobalFilter: true,
    debugTable: false,
  });




  // Referencias para virtualización
  const tableContainerRef = useRef(null);
  const headerRef = useRef(null);

  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  // Sincronizar scroll horizontal del header con el body (omisiones)
  const handleBodyScroll = useCallback(() => {
    if (headerRef.current && tableContainerRef.current) {
      headerRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  }, []);

  const { rows } = table.getRowModel();

  // Medir ancho de la scrollbar vertical del body para compensar el header
  useEffect(() => {
    if (isOmisiones && tableContainerRef.current) {
      const el = tableContainerRef.current;
      const width = el.offsetWidth - el.clientWidth;
      setScrollbarWidth(width);
    }
  }, [isOmisiones, rows.length]);

  // Configurar el virtualizador
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  // Calcular la altura total de la tabla
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  // Anchos fijos para omisiones (en px para evitar compresión)
  const OMISIONES_MIN_WIDTH = '1740px';

  const headerColumnsLayout = isOmisiones
    ? [
      { key: 'fecha', label: 'Fecha', width: '145px', align: 'left' },
      { key: 'alerta', label: 'Alerta', width: '170px', align: 'left' },
      { key: 'vehiculo', label: 'Vehículo', width: '150px', align: 'left' },
      { key: 'valor', label: 'Valor', width: '60px', align: 'center' },
      { key: 'codigo', label: 'Código', width: '90px', align: 'center' },
      { key: 'tiempo_abierto', label: 'T. Abierto', width: '100px', align: 'center' },
      { key: 'chofer', label: 'Chofer', width: '140px', align: 'left' },
      { key: 'geocerca', label: 'Marcadores', width: '130px', align: 'left' },
      { key: 'coordenadas', label: 'Coordenadas', width: '155px', align: 'left' },
      { key: 'atendido', label: 'Atendido', width: '80px', align: 'center' },
      { key: 'comentario', label: 'Comentario', width: '130px', align: 'left' },
      { key: 'atendido_por', label: 'Atendido Por', width: '120px', align: 'left' },
      { key: 'fecha_atendido', label: 'Fecha Atendido', width: '155px', align: 'left' },
      { key: 'ubicacion', label: 'Mapa', width: '50px', align: 'center' },
      { key: 'video', label: 'Video', width: '50px', align: 'center' },
    ]
    : showExtendedColumns
      ? [
        { key: 'fecha', label: 'Fecha', width: '12%', align: 'left' },
        { key: 'alerta', label: 'Alerta', width: '16%', align: 'left' },
        { key: 'vehiculo', label: 'Vehículo', width: '14%', align: 'left' },
        { key: 'random', label: 'Random', width: '7%', align: 'left' },
        { key: 'tiempo_abierto', label: 'T. Abierto', width: '7%', align: 'left' },
        { key: 'chofer', label: 'Chofer', width: '10%', align: 'left' },
        { key: 'geocerca', label: 'Marcadores', width: '10%', align: 'left' },
        { key: 'coordenadas', label: 'Coordenadas', width: '13%', align: 'left' },
        { key: 'ubicacion', label: 'Mapa', width: '4%', align: 'center' },
        { key: 'video', label: 'Video', width: '4%', align: 'center' }
      ]
      : [
        { key: 'fecha', label: 'Fecha', width: '13%', align: 'left' },
        { key: 'alerta', label: 'Alerta', width: '18%', align: 'left' },
        { key: 'vehiculo', label: 'Vehículo', width: '15%', align: 'left' },
        { key: 'chofer', label: 'Chofer', width: '12%', align: 'left' },
        { key: 'geocerca', label: 'Marcadores', width: '13%', align: 'left' },
        { key: 'coordenadas', label: 'Coordenadas', width: '14%', align: 'left' },
        { key: 'ubicacion', label: 'Mapa', width: '4%', align: 'center' },
        { key: 'video', label: 'Video', width: '4%', align: 'center' }
      ];

  const cellWidthConfig = isOmisiones
    ? {
      fecha: '145px',
      alerta: '170px',
      vehiculo: '150px',
      valor: '60px',
      codigo: '90px',
      tiempo_abierto: '100px',
      chofer: '140px',
      geocerca: '130px',
      coordenadas: '155px',
      atendido: '80px',
      comentario: '130px',
      atendido_por: '120px',
      fecha_atendido: '155px',
      ubicacion: '50px',
      video: '50px'
    }
    : showExtendedColumns
      ? {
        fecha: '12%',
        alerta: '16%',
        vehiculo: '14%',
        random: '7%',
        tiempo_abierto: '7%',
        chofer: '10%',
        geocerca: '10%',
        coordenadas: '13%',
        ubicacion: '4%',
        video: '4%'
      }
      : {
        fecha: '13%',
        alerta: '18%',
        vehiculo: '15%',
        chofer: '12%',
        geocerca: '13%',
        coordenadas: '14%',
        ubicacion: '4%',
        video: '4%'
      };

  // Helper para aplicar flex fijo en celdas de omisiones (evita compresión)
  const cellStyle = (configWidth, extra = {}) => {
    const base = { width: configWidth, ...extra };
    if (isOmisiones) {
      base.minWidth = configWidth;
      base.flex = '0 0 auto';
    }
    return base;
  };

  // Handlers
  const handleShowMap = useCallback((data) => {
    let marcadorAlerta = obtenerMarcadorAlertas(data);
    let marcadores = obtenerMarcadoresAlerta(data);
    setSelectedLocation([marcadorAlerta, ...marcadores,]);
    setShowMapModal(true);
  }, []);

  const handleShowVideoModal = useCallback((vehicle) => {
    setSelectedVehicle(vehicle);
    setShowVideoModal(true);
  }, []);

  const handleShowMarcadorModal = useCallback((data) => {
    setShowMarcadorModal(true);
    setContentMarcadorModal(data);
  }, []);

  const TabTitle = ({ amount, percentage, type, updown, action }) => (
    <div className="d-flex flex-column align-items-start">
      <div className="d-flex align-items-center justify-content-start">
        <h6 className="mb-0 me-2 ">{amount}</h6>

        <FontAwesomeIcon
          icon={(updown === undefined ? percentage : updown >= 0) ? 'caret-up' : 'caret-down'}
          className={classNames('me-1', {
            'text-success': type === 'up',
            'text-danger': type === 'down',
            'text-primary': type === 'standard',
            'text-warning': type === 'warning',
            'text-info': type === 'pending'
          })}
        />


        <a
          className={classNames({
            'text-success': type === 'up',
            'text-danger': type === 'down',
            'text-primary': type === 'standard',
            'text-warning': type === 'warning',
            'text-info': type === 'pending'
          })}
          href="#!"
          onClick={action}
        >
          {percentage}
        </a>

      </div>


    </div>
  );

  const obtenerIcono = (alerta_real, tipo_alerta) => {
    var icono = obtenerIconoAlerta(alerta_real);
    if (title.toLowerCase().includes(' gps')) {
      icono = obtenerIconoAlerta(alerta_real)
    }
    else if (title.toLowerCase().includes(' omisiones')) {
      icono = obtenerIconoAlertaOmisiones(tipo_alerta)
    }
    else if (title.toLowerCase().includes(' ia')) {
      icono = obtenerIconoAlertaAI(alerta_real)
    }
    const src = icono?.props?.src;
    return icono;
  };

  const obtenerMarcadorAlertas = (alerta, isMarkerColumn = false) => {
    var index = 0;
    if (alerta.alerta_real?.includes("Movimiento")) return null;
    index += 1;
    console.log('alerta debug:', alerta);
    var alertaStr = (alerta.alerta_real || '');
    if (alerta.valor) {
      if (/^[0-9]+(\.[0-9]+)?$/.test(alerta.valor)) {
        alertaStr += ' / ' + (Math.floor(parseFloat(alerta.valor + '')) + ' min');
      } else {
        alertaStr += ' / ' + alerta.valor;
      }
    }
    //let arregloFecha = (alerta.fecha || '').split(' ');
    if (isMarkerColumn === false) {
      setInfoView(obtenerInfoViewTop({
        markerId: index,
        titulo: alerta.no_economico || '',
        colorFondoTitulo: alerta.color_vehiculo || 'black',
        colorTextoTitulo: 'text-white',
        velocidad: alerta.velocidad || '',
        alerta: alertaStr,
        fecha: dayjs(alerta.fecha).format('DD/MM/YYYY HH:mm:ss') || '',
        ruta: alerta.distancia ? (alerta.distancia).toFixed(3) : 'N/A',
        chofer: alerta.chofer || 'N/A',
        alerta_omisiones: ''//alerta.alerta_omisiones
      }))
    }
    return isMarkerColumn === false ? {
      lat: parseFloat(alerta.latitud),
      lng: parseFloat(alerta.longitud),
      icon: obtenerIcono(alerta.tipo_real, alerta.tipo_alerta),
      radio: alerta.radio,
      title: alerta.alerta_omisiones ? alerta.alerta_grupo : alerta.alerta

    } :
      {
        lat: parseFloat(alerta.latitud),
        lng: parseFloat(alerta.longitud),
        icon: obtenerIcono(alerta.tipo_real, alerta.tipo_alerta),
        radio: alerta.radio,

      };
  };
  const obtenerMarcadoresAlerta = (rowData) => {
    const nombreGeocercas = (Array.isArray(rowData.geoceca) ? rowData.geoceca.map(c => c.Nombre).join(',') || '' : (rowData.geoceca || '')).toLowerCase().split(',');
    const filtradas = (store.geocercas.filter(g => nombreGeocercas.includes(g.nombre.toLowerCase())));
    setGeocercasFiltradas(filtradas || []);
    const arregloGeocercas = filtradas.map(g => {
      const sumaTotales = g.puntos.reduce(
        (acc, punto) => {
          acc.lat += punto.latitud;
          acc.lng += punto.longitud;
          return acc;
        },
        { lat: 0, lng: 0 }
      );
      const total = g.puntos.length;
      return ({
        ...g,
        lat: sumaTotales.lat / total,
        lng: sumaTotales.lng / total
      })
    });
    const markers = [
      ...((rowData.cliente || []).map(c => {
        let alerta = {
          latitud: c.Location.coordinates[1],
          longitud: c.Location.coordinates[0],
          tipo_real: 99990,
          tipo_alerta: 99990,
          alerta_real: c.Nombres,
          radio: c.Radio,
        };
        return obtenerMarcadorAlertas(alerta, true);
      }) || []),
      ...((arregloGeocercas || []).map(c => {
        let alerta = {
          latitud: c.lat,
          longitud: c.lng,
          tipo_real: 99991,
          tipo_alerta: 99991,
          alerta_real: c.nombre
        };
        return obtenerMarcadorAlertas(alerta, true);
      }) || []),
      ...((rowData.referencia || []).map(c => {
        let alerta = {
          latitud: c.Location.coordinates[1],
          longitud: c.Location.coordinates[0],
          tipo_real: 99992,
          tipo_alerta: 99992,
          radio: c.Radio,
          alerta_real: c.Nombres
        };
        return obtenerMarcadorAlertas(alerta, true);
      }) || [])
    ];
    return markers;
  };
  const getLabelMarcador = (rowData) => {

    //Parametros de entrada
    const paramsEntry = {
      Clientes: (rowData.cliente || []).map(c => c.Nombres).join(',') || '',
      Geocercas: (Array.isArray(rowData.geoceca) ? rowData.geoceca.map(c => c.Nombre).join(',') || '' : (rowData.geoceca || '')),
      Referencias: (rowData.referencia || []).map(c => c.Nombres).join(',') || ''
    };

    //Obtener propiedades con valor no vacío
    const propiedadesConValor = Object.entries(paramsEntry).filter(
      ([_, valor]) => valor.trim() !== ""
    );

    //Almacena los parametros para mostrarlos en el modal
    const contentModal = propiedadesConValor.map((item, index) => {
      return (
        {
          key: item[0],
          value: item[1]
        }
      )
    });

    //Cantidad datos con valor
    const cantidad = Object.values(paramsEntry)
      .flatMap(str => str.split(','))
      .filter(item => item.trim() !== '') // elimina vacíos y espacios
      .length;
    const style = {
      display: "-webkit-box",
      WebkitLineClamp: 1, // Limita a 2 líneas
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "normal",
    };
    return (
      <div>
        {cantidad === 1 ? (

          <div>
            <strong style={style} title={`${propiedadesConValor[0][1]}`}>{propiedadesConValor[0][1]}</strong>
            <span>{propiedadesConValor[0][0]}</span>
          </div>

          // <p>{propiedadesConValor[0][1]}</p>
        ) : cantidad == 0 ? (<span className="text-muted">N/A</span>) : (
          <Col>
            <Row >

              <TabTitle
                amount={`${cantidad}`}
                percentage={'Ver'}
                updown={1}
                type='pending'
                action={() => {
                  handleShowMarcadorModal(contentModal)
                }}
              />
            </Row>
            <Row >
              <span>Marcadores </span>
            </Row>

          </Col>
        )}
      </div>
    );
  };

  const handleToggleVideo = useCallback((event, index) => {
    let _urlVideo = '';
    if (event.target.checked) {
      setConsultandoCamara(prev => {
        const items = [...prev];
        items[index] = true;
        return items;
      });
      if (selectedVehicle && selectedVehicle.id_camara) {
        console.log("selectedVehicle:", selectedVehicle);
        console.log('Consultando cámara:', selectedVehicle.id_camara);

        const esOmisiones = title.toLowerCase().includes('omisiones') || title.toLowerCase().includes('omision');
        var fechaParseada;
        if (esOmisiones) {
          fechaParseada = dayjs.utc(selectedVehicle.fecha).local();
        } else {
          fechaParseada = dayjs(selectedVehicle.fecha, 'YYYY-MM-DD HH:mm:ss');
        }
        const decoded = jwtDecode(localStorage.getItem('token'));
        CamarasService.getHistorialDeVisualizacion({
          id_camara: selectedVehicle.id_camara,
          canal: index,
          fecha_inicio_ticks: fechaParseada,
          fecha_fin_ticks: fechaParseada,
          empresaUid: decoded.uid_empresa,
          uid_usuario: decoded.uid_usuario
        }).then(resp => {
          if (resp.StatusCode === 200 && resp.Data !== null) {
            _urlVideo = resp.Data.Url;
            setUrlVideo(prev => {
              const items = [...prev];
              items[index] = _urlVideo;
              return items;
            });
          } else {
            setVideoChecks(prev => {
              const newChecks = [...prev];
              newChecks[index] = false;
              return newChecks;
            });
            MySwal.fire({
              icon: 'error',
              text: !resp.data ? 'No se pudo obtener la información' : resp.message,
              showConfirmButton: true
            });
          }
          setConsultandoCamara(prev => {
            const items = [...prev];
            items[index] = false;
            return items;
          });
        });
      }
    } else {
      setUrlVideo(prev => {
        const items = [...prev];
        items[index] = '';
        return items;
      });
    }
    setVideoChecks(prev => {
      const newChecks = [...prev];
      newChecks[index] = !newChecks[index];
      return newChecks;
    });
  }, [selectedVehicle, MySwal]);

  const limpiarModal = useCallback(() => {
    setShowVideoModal(false);
    setVideoChecks([false, false, false, false]);
    setUrlVideo(["", "", "", ""]);
  }, []);

  // Manejar el cambio en la búsqueda global
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      setGlobalFilter(value);
      if (onSearch) onSearch(value);
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, 400); // 400ms debounce
  };

  const handleSearchClear = () => {
    // Limpiar el filtro inmediatamente
    setGlobalFilter('');
    setInputValue(''); // <-- Asegura que el input se limpie visualmente
    if (onSearch) {
      onSearch('');
    }

    // Resetear a la primera página
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  // Manejar cambio en el tamaño de página
  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPagination(prev => ({
      ...prev,
      pageSize: newSize,
    }));
    if (onPageSizeChange) onPageSizeChange(newSize);
  };

  // Manejar exportación a Excel
  // const handleExportToExcel = async () => {

  //   if (window['guardarBitacoraExport']) {
  //     try {
  //       window['guardarBitacoraExport']();
  //     } catch (error) {

  //     }
  //   }
  //   if (typeof onExport === 'function') {
  //     onExport();
  //     return;
  //   }

  //   try {
  //     // Default export: exporta todos los datos filtrados
  //     const dataToExport = table.getFilteredRowModel().rows.map(row => {
  //       return {
  //         //Fecha: formatFecha(row.original.fecha),
  //         Fecha: formatFecha(row.original.fecha, title),
  //         Alerta: row.original.alerta,
  //         Vehiculo: row.original.no_economico,
  //         Chofer: row.original.chofer || 'N/A',
  //         Geocerca: row.original.geocerca || 'N/A'
  //       };
  //     });

  //     const ws = XLSX.utils.json_to_sheet(dataToExport);
  //     const wb = XLSX.utils.book_new();

  //     // Agregar título
  //     XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: "A1" });

  //     // Combinar celdas para el título
  //     if (!ws['!merges']) ws['!merges'] = [];
  //     ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } });

  //     // Ajustar anchos de columna
  //     const colWidths = [];
  //     for (let i = 0; i < 5; i++) colWidths.push({ wch: 15 });
  //     ws['!cols'] = colWidths;

  //     XLSX.utils.book_append_sheet(wb, ws, "Alertas");
  //     XLSX.writeFile(wb, "ReporteDeAlertas.xlsx");
  //   } catch (error) {
  //     console.error("Error al exportar a Excel:", error);
  //   }
  // };

  const handleExportToExcelV1 = () => {

    if (window['guardarBitacoraExport']) {
      try {
        window['guardarBitacoraExport']();
      } catch (error) {

      }
    }
    if (typeof onExport === 'function') {
      onExport();
      return;
    }

    try {

      // Default export: exporta todos los datos filtrados
      const dataToExport = table.getFilteredRowModel().rows.map(row => {
        return {
          Fecha: formatFecha(row.original.fecha, title),
          Alerta: row.original.alerta_real,
          Vehiculo: row.original.no_economico,
          Chofer: row.original.chofer || 'N/A',

          Clientes: (row.original.cliente || []).map(c => c.Nombres).join(',') || '',
          Geocerca: (Array.isArray(row.original.geoceca) ? row.original.geoceca.map(c => c.Nombre).join(',') || '' : (row.original.geoceca || '')),
          Referencias: (row.original.referencia || []).map(c => c.Nombres).join(',') || ''
        };
      });

      debugger;
      const groupedData = dataToExport.reduce((accumulator, currentItem) => {

        // Obtiene identificador del vehículo
        const key = currentItem.Vehiculo;

        if (!accumulator[key]) {
          accumulator[key] = {
            titulo: currentItem.Vehiculo,
            detalles: [],
          };
        }

        //Agregar registro
        accumulator[key].detalles.push(currentItem);

        return accumulator;
      }, {});

      const formattedData = Object.values(groupedData).map((item, index) => ({
        ...item
      }));

      //Titulo del documento
      const now = new Date().toLocaleDateString();
      const titleDoc = 'Reporte de Alertas';

      // Agregar cabecera
      let sheetData = [];
      sheetData.push(['', '', `           Quro GPS    `]);
      sheetData.push(['', '', `       01 999-944-27-00  `]);
      sheetData.push(['', '', `    soporte@quro.com.mx `]);
      sheetData.push([`Fecha: ${now}`]);
      sheetData.push([`Título: ${titleDoc}`]);
      sheetData.push([]); // espacio

      // Agregar maestro-detalle
      formattedData.forEach((grupo, index) => {

        // Fila de maestro
        sheetData.push([
          `Vehiculo: ${grupo.titulo}    |    Total Alertas: ${grupo.detalles.length}`
        ]);

        // Encabezados de los detalles
        sheetData.push(['Fecha', 'Alerta', 'Vehiculo', 'Chofer', 'Cliente', 'Geocerca', 'Referencia']);

        // Filas de detalles
        grupo.detalles.forEach(detalle => {
          sheetData.push([
            detalle.Fecha,
            detalle.Alerta,
            detalle.Vehiculo,
            detalle.Chofer,
            detalle.Clientes,
            detalle.Geocerca,
            detalle.Referencias,
          ]);
        });

        // Línea vacía entre grupos
        sheetData.push([]);
      });

      // Crear hoja
      const ws = XLSX.utils.aoa_to_sheet(sheetData);

      //Agrupar los documentos 
      // hoja["!merges"] = [
      //   XLSX.utils.decode_range("A1:E1"),
      //   XLSX.utils.decode_range("A2:E2"),
      // ];

      // Ajustar anchos de columna
      const colWidths = [];
      for (let i = 0; i < 7; i++) colWidths.push({ wch: 25 });
      ws['!cols'] = colWidths;

      // Crear libro
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Alertas");

      // Exportar
      XLSX.writeFile(wb, "Reporte_Alertas.xlsx");

    } catch (error) {
      console.error("Error al exportar a Excel:", error);
    }
  };

  const handleExportToExcel = async () => {

    try {
      if (window['guardarBitacoraExport']) {
        try {
          window['guardarBitacoraExport']();
        } catch (error) {

        }
      }

      // Default export: exporta todos los datos filtrados
      const dataToExport = table.getFilteredRowModel().rows.map(row => {
        const isClosed = (row.original?.alerta || '').toLowerCase().includes('cerrad');
        const base = {
          Fecha: formatFecha(row.original.fecha, title),
          Alerta: row.original.alerta_real,
          Vehiculo: row.original.no_economico,
          ...(isOmisiones ? {
            Valor: row.original.valor ?? 'N/A',
            Código: row.original.codigoaleatorio || '',
            'T. Abierto': row.original.tiempo_abierto_calc || row.original.tiempo_abierto || '--'
          } : {}),
          // Random y Tiempo Abierto para modo extended (no-omisiones)
          ...(showExtendedColumns && !isOmisiones ? {
            Random: row.original?.random ?? row.original?.codigoaleatorio ?? 'N/A',
            'T. Abierto': isClosed ? (row.original?.tiempo_abierto || '--') : '--'
          } : {}),
          Chofer: row.original.chofer || 'N/A',
          Latitud: row.original.latitud?.toString() || '0',
          Longitud: row.original.longitud?.toString() || '0',
          Coordenadas: `${row.original.latitud || 0}, ${row.original.longitud || 0}`,
          Clientes: (row.original.cliente || []).map(c => c.Nombres).join(',') || '',
          Geocerca: (Array.isArray(row.original.geoceca) ? row.original.geoceca.map(c => c.Nombre).join(',') || '' : (row.original.geoceca || '')),
          Referencias: (row.original.referencia || []).map(c => c.Nombres).join(',') || '',
          Dirección: row.original.direccion || '',
          Video: row.original.vehiculo_con_camara && row.original.camara_en_linea ? 'Disponible' : 'No',
          ...(isOmisiones ? {
            Atendido: row.original.Atendido === 1 ? 'Sí' : 'No',
            Comentario: row.original.Comentario || '',
            'Atendido Por': row.original.Nombre_Usuario_Atendido || '',
            'Fecha Atendido': row.original.FechaHora_Atendido
              ? dayjs.utc(row.original.FechaHora_Atendido).local().format('DD/MM/YYYY HH:mm:ss')
              : 'N/A'
          } : {})
        };
        return base;
      });

      const groupedData = dataToExport.reduce((accumulator, currentItem) => {

        // Obtiene identificador del vehículo
        const key = currentItem.Vehiculo;

        if (!accumulator[key]) {
          accumulator[key] = {
            titulo: currentItem.Vehiculo,
            detalles: [],
          };
        }

        //Agregar registro
        accumulator[key].detalles.push(currentItem);

        return accumulator;
      }, {});

      const formattedData = Object.values(groupedData).map((item, index) => ({
        ...item
      }));

      //Columnas del reporte
      let columns = isOmisiones
        ? ['Fecha', 'Alerta', 'Vehiculo', 'Valor', 'Código', 'T. Abierto', 'Chofer', 'Latitud', 'Longitud', 'Coordenadas', 'Cliente', 'Geocerca', 'Referencia', 'Dirección', 'Video', 'Atendido', 'Comentario', 'Atendido Por', 'Fecha Atendido']
        : showExtendedColumns
          ? ['Fecha', 'Alerta', 'Vehiculo', 'Random', 'T. Abierto', 'Chofer', 'Latitud', 'Longitud', 'Coordenadas', 'Cliente', 'Geocerca', 'Referencia', 'Dirección', 'Video']
          : ['Fecha', 'Alerta', 'Vehiculo', 'Chofer', 'Latitud', 'Longitud', 'Coordenadas', 'Cliente', 'Geocerca', 'Referencia', 'Dirección', 'Video']

      // Agregar maestro-detalle 
      let dataReport = [];

      formattedData.forEach((grupo, index) => {

        //Detallado por vehiculo 
        let subList = [];

        // Filas de detalles
        grupo.detalles.forEach(detalle => {

          const row = [
            detalle.Fecha?.toString() || "N/A",
            detalle.Alerta?.toString() || "N/A",
            detalle.Vehiculo?.toString() || "N/A",
            ...(isOmisiones ? [
              detalle.Valor?.toString() || "N/A",
              detalle['Código']?.toString() || "",
              detalle['T. Abierto']?.toString() || "--",
            ] : []),
            ...(showExtendedColumns && !isOmisiones ? [
              detalle.Random?.toString() || "N/A",
              detalle['T. Abierto']?.toString() || "--",
            ] : []),
            detalle.Chofer?.toString() || "N/A",
            detalle.Latitud?.toString() || "0",
            detalle.Longitud?.toString() || "0",
            detalle.Coordenadas?.toString() || "0, 0",
            detalle.Clientes?.toString() || "N/A",
            detalle.Geocerca?.toString() || "N/A",
            detalle.Referencias?.toString() || "N/A",
            detalle['Dirección']?.toString() || "",
            detalle.Video?.toString() || "No",
            ...(isOmisiones ? [
              detalle.Atendido?.toString() || "N/A",
              detalle.Comentario?.toString() || "",
              detalle['Atendido Por']?.toString() || "N/A",
              detalle['Fecha Atendido']?.toString() || "N/A",
            ] : []),
          ];
          subList.push(row);
        });

        //maestro
        let masterData =
        {
          titulo: `Vehiculo: (${grupo.titulo}) `,
          detalles: subList
        }

        //Agrega el maestro a la lista de informacion
        dataReport.push(masterData);
      });

      //Exportar reporte 
      generateExcelReport('Reporte Alertas', dataReport, true, columns);

    } catch (error) {
      console.error("Error al exportar a Excel:", error);
    }
  };

  const prepareForPrint = useCallback(() => {
    console.log('🖨️ Preparando tabla para impresión...');

    if (tableContainerRef.current) {
      const container = tableContainerRef.current;

      // ✅ EXPANDIR CONTENEDOR
      container.style.height = 'auto';
      container.style.maxHeight = 'none';
      container.style.overflow = 'visible';

      // ✅ MARCAR COMO MODO IMPRESIÓN
      container.setAttribute('data-print-mode', 'true');
      container.classList.add('print-mode');
    }

    // ✅ FORZAR RENDERIZADO DE TODAS LAS FILAS
    setPagination(prev => ({
      ...prev,
      pageSize: data.length, // Mostrar todos los datos
      pageIndex: 0
    }));

    return new Promise(resolve => {
      // Esperar que se apliquen los cambios
      setTimeout(() => {
        console.log('✅ Tabla preparada para impresión');
        resolve();
      }, 500);
    });
  }, [data.length]);

  const restoreAfterPrint = useCallback(() => {
    console.log('🔄 Restaurando tabla después de impresión...');

    if (tableContainerRef.current) {
      const container = tableContainerRef.current;

      // ✅ RESTAURAR ESTILOS ORIGINALES
      container.style.height = 'calc(700px - 220px)';
      container.style.maxHeight = '';
      container.style.overflow = 'auto';

      // ✅ QUITAR MODO IMPRESIÓN
      container.removeAttribute('data-print-mode');
      container.classList.remove('print-mode');
    }

    // ✅ RESTAURAR PAGINACIÓN
    setPagination(prev => ({
      ...prev,
      pageSize: pageSize, // Volver al tamaño original
      pageIndex: 0
    }));
  }, [pageSize]);

  React.useEffect(() => {
    window.prepareTableForPrint = prepareForPrint;
    window.restoreTableAfterPrint = restoreAfterPrint;

    return () => {
      delete window.prepareTableForPrint;
      delete window.restoreTableAfterPrint;
    };
  }, [prepareForPrint, restoreAfterPrint]);

  return (
    <GoogleMapsProvider>
      <Card className="card h-100 d-flex flex-column" data-print="true" data-component="table">
        <Card.Header className='card-header' data-print="true">
          <Row className="d-flex align-items-center justify-content-between">
            <Col md={6}>
              <h6 className="mb-0 text-nowrap py-2 py-xl-0" data-table-title="true">{title}</h6>
            </Col>
            <Col md={4} className="d-flex justify-content-end align-items-center">
              <Button
                variant="primary"
                size="sm"
                className="me-2 p-2"
                onClick={handleExportToExcel}
                disabled={isExporting || rows.length === 0}
              >
                {isExporting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-1"
                    />
                    Exportando...
                  </>
                ) : (
                  <>
                    <img
                      src={iconoPng}
                      alt="Exportar" title='Exportar a Excel'
                      style={{ width: '20px', height: '20px', filter: 'brightness(0) invert(1)' }} // Ajusta el tamaño
                      className="me-1 ms-1"
                    />
                    {/* <FaFileExport className="me-1 ms-1" /> */}
                  </>
                )}
              </Button>

              {/* FILTRO ATENDIDO - Solo omisiones */}
              {isOmisiones && (
                <Form.Select
                  size="sm"
                  className="me-2 p-2"
                  style={{ maxWidth: 140 }}
                  value={filtroAtendido}
                  onChange={(e) => setFiltroAtendido(e.target.value)}
                >
                  <option value="todos">Todos</option>
                  <option value="si">Atendidos</option>
                  <option value="no">No atendidos</option>
                </Form.Select>
              )}

              {/* FILTRO GLOBAL */}
              <InputGroup size="sm" style={{ maxWidth: 200 }}>
                <Form.Control
                  className='p-2'
                  type="text"
                  placeholder="Buscar..."
                  value={inputValue || ''}
                  onChange={handleSearchChange}
                />
                {globalFilter && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      handleSearchClear();
                    }}
                  >
                    &times;
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={2}>

              {/*SELECTOR: Visualizador de cantidad de filas*/}
              <Form className='d-flex p-2 flex-row-reverse'>
                <Form.Select
                  className='p-2'
                  size="sm"
                  style={{ width: '100px' }}
                  value={table.getState().pagination.pageSize}
                  onChange={handlePageSizeChange}
                >
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="250">250</option>
                  <option value="500">500</option>
                  <option value="1000">1000</option>
                </Form.Select>

              </Form>
            </Col>
          </Row>
        </Card.Header>

        {/* CARD BODY: Contenido de la tabla*/}
        <Card.Body className="p-0 card-body flex-grow-1" data-print="true">
          <style>{customStyles}</style>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Cargando...</span>
              </Spinner>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center p-5">No hay datos para mostrar</div>
          ) : (
            <div>
              {/* Cabecera de la tabla - scroll sincronizado con body */}
              <div ref={headerRef} style={{ overflowX: 'hidden' }}>
                <div className="d-flex table-header ps-3 text-nowrap align-middle" style={{ paddingRight: isOmisiones ? `${scrollbarWidth}px` : '1.25rem', minWidth: isOmisiones ? OMISIONES_MIN_WIDTH : undefined, flexWrap: 'nowrap' }} data-table-headers="true">
                  {headerColumnsLayout.map(({ key, label, width, align }) => (
                    <div
                      key={key}
                      style={{
                        width,
                        minWidth: isOmisiones ? width : undefined,
                        flex: isOmisiones ? '0 0 auto' : undefined,
                        textAlign: align ?? 'left'
                      }}
                      data-column={key}
                    >
                      {label}
                    </div>
                  ))}
                </div>
              </div>
              <div
                ref={tableContainerRef}
                onScroll={isOmisiones ? handleBodyScroll : undefined}
                style={{
                  height: 'calc(700px - 220px)',
                  overflow: 'auto',
                }}
                className="list-striped"
              >
                <div
                  style={{
                    height: `${totalSize}px`,
                    width: '100%',
                    minWidth: isOmisiones ? OMISIONES_MIN_WIDTH : undefined,
                    position: 'relative',
                  }}
                >
                  {virtualRows.map(virtualRow => {
                    const row = rows[virtualRow.index];
                    var iconoAlerta = <div className={{ width: '40px', height: '40px' }}></div>;

                    if (title.toLowerCase().includes(' gps')) {
                      iconoAlerta = obtenerImgAlerta(row.original.tipo_real)
                    }
                    else if (title.toLowerCase().includes(' omisiones')) {
                      iconoAlerta = obtenerImgAlertaOmisiones(row.original.tipo_alerta)
                    }
                    else if (title.toLowerCase().includes(' ia')) {
                      iconoAlerta = obtenerImgAlertaCamara(row.original.tipo_real)
                    }

                    const style1Lines = {
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "normal",
                    };
                    const isClosedAlert = (row.original?.alerta || '').toLowerCase().includes('cerrad');
                    const displayDuration = isClosedAlert ? (row.original?.tiempo_abierto || '--') : '--';
                    return (
                      <div
                        key={row.id}
                        className="alert-row d-flex"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          minWidth: isOmisiones ? OMISIONES_MIN_WIDTH : undefined,
                          height: `${ROW_HEIGHT}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                          alignItems: 'center',
                          flexWrap: 'nowrap',
                          borderBottom: '1px solid #f5f5f5',
                        }}
                      >
                        <div style={cellStyle(cellWidthConfig.fecha, { marginLeft: '.5rem', ...style1Lines })} data-cell="fecha" title={formatFecha(row.original.fecha, title)} >
                          {formatFecha(row.original.fecha, title)}
                        </div>
                        <div className='px-1' style={cellStyle(cellWidthConfig.alerta)} data-cell="alerta">
                          <div className="d-flex align-items-center" >
                            <Col md={2}>
                              {iconoAlerta}
                            </Col>
                            <Col >
                              <Row style={style1Lines} title={row.original.alerta}>
                                <strong>{row.original.alerta}</strong>
                              </Row>
                              <Row style={style1Lines} title={row.original.alerta_grupo}>
                                <span>{row.original.alerta_grupo}</span>
                              </Row>
                            </Col>
                          </div>
                        </div>
                        <div className='px-1' style={cellStyle(cellWidthConfig.vehiculo, { textAlign: 'left', ...style1Lines })} data-cell="vehiculo" title={row.original.no_economico}> {row.original.no_economico.substring(0, 25)} </div>

                        {isOmisiones && (
                          <div className='px-1' style={cellStyle(cellWidthConfig.valor, { textAlign: 'center', ...style1Lines })} data-cell="valor">
                            {row.original.valor ?? 'N/A'}
                          </div>
                        )}

                        {isOmisiones && (
                          <div className='px-1' style={cellStyle(cellWidthConfig.codigo, { textAlign: 'center', ...style1Lines })} data-cell="codigo">
                            {row.original.codigoaleatorio || '--'}
                          </div>
                        )}

                        {isOmisiones && (
                          <div className='px-1' style={cellStyle(cellWidthConfig.tiempo_abierto, { textAlign: 'center', ...style1Lines })} data-cell="tiempo_abierto">
                            {row.original.tiempo_abierto_calc || '--'}
                          </div>
                        )}

                        {showExtendedColumns && !isOmisiones && (
                          <div className='px-1' style={{ width: cellWidthConfig.random, ...style1Lines }} data-cell="random" title={`${row.original.random ?? 'N/A'}`}>
                            {`${row.original.random ?? 'N/A'}`.substring(0, 20)}
                          </div>
                        )}

                        {showExtendedColumns && !isOmisiones && (
                          <div className='px-1' style={{ width: cellWidthConfig.tiempo_abierto, ...style1Lines }} data-cell="tiempo_abierto" title={displayDuration}>
                            {displayDuration}
                          </div>
                        )}

                        <div className='px-1' style={cellStyle(cellWidthConfig.chofer, { ...style1Lines })} data-cell="chofer" title={row.original.chofer || <span className="text-muted">N/A</span>} >{row.original.chofer ? (row.original.chofer.substring(0, 20)) : ('N/A')} </div>

                        <div className='px-1' style={cellStyle(cellWidthConfig.geocerca)} data-cell="geocerca">
                          {getLabelMarcador(row.original)}
                        </div>

                        <CopyCoordCell
                          lat={row.original.latitud}
                          lng={row.original.longitud}
                          style={cellStyle(cellWidthConfig.coordenadas)}
                        />

                        {isOmisiones && (
                          <>
                            <div className='px-1' style={cellStyle(cellWidthConfig.atendido, { textAlign: 'center' })} data-cell="atendido">
                              <Badge bg={row.original.Atendido === 1 ? 'success' : 'danger'} style={{ fontSize: '0.7em' }}>
                                {row.original.Atendido === 1 ? 'Sí' : 'No'}
                              </Badge>
                            </div>
                            <div className='px-1' style={cellStyle(cellWidthConfig.comentario, { ...style1Lines })} data-cell="comentario" title={row.original.Comentario || ''}>
                              {(row.original.Comentario || 'N/A').substring(0, 20)}
                            </div>
                            <div className='px-1' style={cellStyle(cellWidthConfig.atendido_por, { ...style1Lines })} data-cell="atendido_por" title={row.original.Nombre_Usuario_Atendido || ''}>
                              {(row.original.Nombre_Usuario_Atendido || 'N/A').substring(0, 15)}
                            </div>
                            <div className='px-1' style={cellStyle(cellWidthConfig.fecha_atendido, { ...style1Lines })} data-cell="fecha_atendido">
                              {row.original.FechaHora_Atendido
                                ? dayjs.utc(row.original.FechaHora_Atendido).local().format('DD/MM/YYYY HH:mm:ss')
                                : 'N/A'}
                            </div>
                          </>
                        )}

                        <div className='px-1' style={cellStyle(cellWidthConfig.ubicacion, { textAlign: 'center' })} data-cell="ubicacion">
                          <a
                            size="sm"
                            className="p-1"
                            href='#!'
                            onClick={() => handleShowMap(row.original)}
                          >
                            <FaMapMarkerAlt className="text-danger" />
                          </a>
                        </div>

                        <div className='px-1' style={cellStyle(cellWidthConfig.video, { textAlign: 'center' })} data-cell="video" >
                            {row.original.vehiculo_con_camara && row.original.camara_en_linea ? (
                              <FaPlayCircle
                                size={20}
                                className="text-danger cursor-pointer"
                                onClick={() => handleShowVideoModal(row.original)}
                                style={{ cursor: 'pointer' }}
                              />
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </Card.Body>

        <Card.Footer className="bg-white card-footer" data-no-print="true">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div className="d-flex align-items-center">
              <span className="me-2">
                Total: {table.getFilteredRowModel().rows.length} | Página {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
              </span>
              <Form.Control
                type="number"
                min="1"
                max={table.getPageCount()}
                value={table.getState().pagination.pageIndex + 1}
                onChange={e => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  table.setPageIndex(page);
                }}
                style={{ width: '70px', height: '31px' }}
                size="sm"
              />
            </div>
            <div className="d-flex justify-content-center">
              <Button
                variant="outline-dark"
                size="sm"
                className="mx-1"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <FaAngleDoubleLeft />
                {/* &laquo; Primera */}
              </Button>
              <Button
                variant="outline-dark"
                size="sm"
                className="mx-1"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <FaAngleLeft />
              </Button>
              {(() => {
                // Determinar qué páginas mostrar
                let pagesArray = [];
                if (table.getPageCount() <= 5) {
                  // Si hay 5 o menos páginas, mostrarlas todas
                  pagesArray = Array.from({ length: table.getPageCount() }, (_, i) => i);
                } else if (table.getState().pagination.pageIndex <= 2) {
                  // Si estamos en las primeras páginas
                  pagesArray = [0, 1, 2, 3, 4];
                } else if (table.getState().pagination.pageIndex >= table.getPageCount() - 3) {
                  // Si estamos en las últimas páginas
                  pagesArray = [
                    table.getPageCount() - 5,
                    table.getPageCount() - 4,
                    table.getPageCount() - 3,
                    table.getPageCount() - 2,
                    table.getPageCount() - 1
                  ];
                } else {
                  // Estamos en medio, mostrar 2 antes y 2 después
                  pagesArray = [
                    table.getState().pagination.pageIndex - 2,
                    table.getState().pagination.pageIndex - 1,
                    table.getState().pagination.pageIndex,
                    table.getState().pagination.pageIndex + 1,
                    table.getState().pagination.pageIndex + 2
                  ];
                }

                return pagesArray.map(pageNum => (
                  <Button
                    key={`page-${pageNum}`}
                    variant={pageNum === table.getState().pagination.pageIndex ? "primary" : "outline-dark"}
                    size="sm"
                    className="mx-1"
                    onClick={() => table.setPageIndex(pageNum)}
                  >
                    {pageNum + 1}
                  </Button>
                ));
              })()}

              <Button
                variant="outline-dark"
                size="sm"
                className="mx-1"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                {/* Siguiente &gt; */}
                <FaAngleRight />
              </Button>
              <Button
                variant="outline-dark"
                size="sm"
                className="mx-1"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                {/* Última &raquo; */}
                <FaAngleDoubleRight />
              </Button>
            </div>
          </div>
        </Card.Footer>
      </Card>

      {/* Modal de mapa */}
      <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Evento: {(selectedLocation.length > 0 ? selectedLocation[0].title : '') || ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={12}>
              {infoView}
              {selectedLocation.length > 0 ? (
                <Suspense fallback={<div className="text-center p-5"><Spinner animation="border" /></div>}>
                  <GoogleMapSelector
                    initialCenter={selectedLocation[0]}
                    markers={selectedLocation}
                    zoomDefault={13}
                    mapStyle="hoper"
                    className="vh-50"
                    showStreetView={false}
                    geocercas={geocercasFiltradas}
                  />
                </Suspense>
              ) : (
                <p>Ubicación no disponible</p>
              )}
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      {/* Modal de video */}
      <Modal show={showVideoModal} onHide={limpiarModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <h5>No Económico: {selectedVehicle?.no_economico}</h5>
            <h6>Fecha: {selectedVehicle ? formatFecha(selectedVehicle.fecha, title) : ''}</h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3 d-flex justify-content-center">
            {videoIds.map((id, index) => (
              <Col xs="auto" key={id} className="text-center">
                <Form.Check
                  id={'chk-' + id}
                  type="checkbox"
                  label={`Canal ${index + 1}`}
                  checked={videoChecks[index]}
                  onChange={(event) => handleToggleVideo(event, index)}
                  className="fw-bold"
                />
              </Col>
            ))}
          </Row>
          <Row>
            {videoIds.map((id, index) => (
              <Col md={6} key={id} className="mb-3 d-flex justify-content-center">
                <div
                  id={`div-` + id}
                  className={consultandoCamara[index]
                    ? `border p-2 text-center w-100 d-flex justify-content-center align-items-center`
                    : `border p-2 text-center w-100`}
                  style={{ height: '200px' }}
                >
                  {consultandoCamara[index] && <Spinner animation="border" role="status" />}
                  {videoChecks[index] && (
                    <Suspense fallback={<Spinner animation="border" />}>
                      <CamaraVisor
                        id={`vid-` + id}
                        vehiculo={selectedVehicle}
                        checked={videoChecks[index]}
                        urlVideo={urlVideo[index]}
                        api_grupo={selectedVehicle.camara_api_grupo}
                      />
                    </Suspense>
                  )}
                </div>
              </Col>
            ))}
          </Row>
        </Modal.Body>
      </Modal>

      {/* Modal de marcadores */}
      <Modal show={showMarcadorModal} onHide={() => setShowMarcadorModal(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <h6> Marcadores </h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3 d-flex justify-content-center">
            <Col md={12}>
              <ListGroup>
                {contentMarcadorModal && contentMarcadorModal.map((item, index) => (
                  <ListGroup.Item>
                    <div>
                      <span>{item.key.toUpperCase()}</span>
                      <br />
                      <strong>{item.value}</strong>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </GoogleMapsProvider>
  );
};

AlertaTableListTanstack.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  searchValue: PropTypes.string,
  onSearch: PropTypes.func,
  pageSize: PropTypes.number,
  onPageSizeChange: PropTypes.func,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  isGroup: PropTypes.bool,
  isExporting: PropTypes.bool,
  onExport: PropTypes.func,
  showExtendedColumns: PropTypes.bool
};

export default AlertaTableListTanstack;